// src/pages/ShopDetailPage.js - Improved version with better error handling and new layout
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
    Layout, Row, Col, Spin, Result, Button, Carousel, Image, 
    Typography, Space, Tag, Rate, Descriptions, Divider, List, Alert, Card, notification, 
    Modal, Input, Radio, Select
} from 'antd';
import { 
    EnvironmentOutlined, PhoneOutlined, GlobalOutlined, ClockCircleOutlined, 
    CheckCircleOutlined, StarFilled, ShopOutlined, TagsOutlined, TeamOutlined, CalendarOutlined, 
    LeftOutlined, RightOutlined, SendOutlined, ExclamationCircleOutlined, EyeOutlined, UserOutlined
} from '@ant-design/icons';
import { fetchShopById } from '../services/shopService';
import { getShopReviews } from '../services/reviewService';
import { isAuthenticated } from '../services/authService';
import ShopMap from '../components/ShopMap';
import BrandsList from '../components/BrandsList';
import ReviewSection from '../components/ReviewSection';
import api from '../services/api';
import NewPricingPlans from '../components/NewPricingPlans';

const { Content } = Layout;
const { Title, Text, Paragraph, Link: AntLink } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Map English day names to German (for opening hours display)
const daysTranslation = {
    'Monday': 'Montag', 'Tuesday': 'Dienstag', 'Wednesday': 'Mittwoch',
    'Thursday': 'Donnerstag', 'Friday': 'Freitag', 'Saturday': 'Samstag', 'Sunday': 'Sonntag'
};
const orderedGermanDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

// Statische Plan-Informationen (könnten auch vom Backend kommen)
const PLANS = {
    premium: { name: 'Premium', monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM_MONTHLY, yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM_YEARLY },
    professional: { name: 'Professional', monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ID_PROFESSIONAL_MONTHLY, yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_ID_PROFESSIONAL_YEARLY },
};

const ShopDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [shop, setShop] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const carouselRef = useRef(); // For Carousel navigation
    const [isClaiming, setIsClaiming] = useState(false); // Ladezustand for Claim-Button
    const [reportSending, setReportSending] = useState(false); // Status für Melde-Button
    const [reportSent, setReportSent] = useState(false); // Status, ob Meldung gesendet wurde
    const [isReportModalVisible, setIsReportModalVisible] = useState(false);
    const [reportReason, setReportReason] = useState('');

    // NEUE STATES für Claim-Modal
    const [isClaimModalVisible, setIsClaimModalVisible] = useState(false);
    const [selectedPlanForClaim, setSelectedPlanForClaim] = useState(null); // Hält { id: '...', interval: '...' }
    const [claimSubmitting, setClaimSubmitting] = useState(false);

    // NEUER STATE für Authentifizierungsstatus
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);

    // Effekt zum Prüfen des Login-Status beim Mounten
    useEffect(() => {
        const checkAuth = async () => {
            const loggedIn = await isAuthenticated(); // Prüft, ob gültiger Token da ist
            setIsUserAuthenticated(loggedIn);
            console.log('ShopDetailPage: User Authenticated Status:', loggedIn);
        };
        checkAuth();
    }, []); // Läuft nur einmal beim Mounten

    // Load shop data with reviews
    useEffect(() => {
        const loadShop = async () => {
            try {
                console.log(`ShopDetailPage: Loading shop with ID: ${id}`);
                setLoading(true);
                setError(null);
                setReportSent(false); // Reset report status on new load

                const response = await fetchShopById(id);
                console.log('ShopDetailPage: Shop fetch response:', response);

                if (response.success && response.data) {
                    setShop(response.data);
                    console.log('ShopDetailPage: Shop data loaded successfully:', response.data);

                    // Load reviews after shop is loaded
                    try {
                        console.log('ShopDetailPage: Loading reviews for shop');
                        const reviewsResponse = await getShopReviews(id);
                        if (reviewsResponse.success && reviewsResponse.data) {
                            setReviews(reviewsResponse.data);
                            console.log(`ShopDetailPage: Loaded ${reviewsResponse.data.length} reviews`);
                        } else {
                            console.warn('ShopDetailPage: Reviews fetch succeeded but no data:', reviewsResponse);
                            setReviews([]);
                        }
                    } catch (reviewError) {
                        console.error('ShopDetailPage: Error loading reviews:', reviewError);
                        setReviews([]);
                        // Don't fail the whole page if reviews can't be loaded
                    }
                } else {
                    console.error('ShopDetailPage: Shop fetch success=false or no data:', response);
                    setError(response.message || 'Shop konnte nicht geladen werden.');
                    setShop(null); // Ensure shop is null on error
                }
            } catch (err) {
                console.error('ShopDetailPage: Error loading shop details:', err);
                console.error('Error stack trace:', err.stack);
                setError('Fehler beim Laden der Shop-Details. Bitte versuchen Sie es später erneut.');
                setShop(null); // Ensure shop is null on error
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadShop();
        } else {
            console.error('ShopDetailPage: No shop ID provided');
            setError('Keine Shop-ID angegeben');
            setLoading(false);
            setShop(null);
        }
    }, [id]);

    // Format opening hours for display
    const formatOpeningHours = useCallback((openingHours) => {
        if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
            console.log('ShopDetailPage: No opening hours data available');
            return orderedGermanDays.reduce((acc, day) => ({ ...acc, [day]: 'Keine Angaben' }), {});
        }

        const formatted = orderedGermanDays.reduce((acc, day) => ({ ...acc, [day]: 'Keine Angaben' }), {});

        openingHours.forEach(day => {
            if (!day.dayOfWeek) return;
            const displayDay = daysTranslation[day.dayOfWeek] || day.dayOfWeek;
            if (!orderedGermanDays.includes(displayDay)) return; // Ensure it's a valid day

            if (!day.opens || !day.closes) {
                formatted[displayDay] = 'Geschlossen';
            } else if (day.opens === '00:00' && day.closes === '23:59') {
                formatted[displayDay] = '24 Stunden geöffnet';
            } else {
                formatted[displayDay] = `${day.opens} - ${day.closes} Uhr`;
            }
        });
        return formatted;
    }, []);

    // Check if shop is currently open
    const isShopOpenNow = useCallback(() => {
        if (!shop || !shop.openingHours || !Array.isArray(shop.openingHours)) {
            return false;
        }

        // Map JS day numbers to day names
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const today = days[new Date().getDay()];
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

        const todayHours = shop.openingHours.find(day => day.dayOfWeek === today);
        if (!todayHours) return false;

        if (!todayHours.opens || !todayHours.closes) return false;

        // Shop is open 24 hours
        if (todayHours.opens === '00:00' && todayHours.closes === '23:59') return true;

        return currentTime >= todayHours.opens && currentTime <= todayHours.closes;
    }, [shop]);

    // Handle new review being added
    const handleReviewAdded = (newReview) => {
        // Add the new review to the list
        setReviews(prevReviews => [newReview, ...prevReviews]);

        // Update shop rating
        if (shop && typeof shop.rating === 'number' && typeof shop.reviewCount === 'number') {
            const newRating = ((shop.rating * shop.reviewCount) + newReview.rating) / (shop.reviewCount + 1);
            setShop(prevShop => ({
                ...prevShop,
                rating: newRating,
                reviewCount: prevShop.reviewCount + 1
            }));
        }
    };

    // Generate Meta Tags for SEO
    const generateMetaTags = useCallback(() => {
        if (loading) {
            return {
                title: 'Lade Shop... | Snus Finder',
                description: 'Details zu einem Snus Shop werden geladen.',
                canonicalUrl: `https://www.snus-finder.de/shops/${id}`
            };
        }
        if (error || !shop) {
            return {
                title: 'Shop nicht gefunden | Snus Finder',
                description: 'Der angeforderte Shop konnte nicht gefunden werden.',
                canonicalUrl: `https://www.snus-finder.de/shops/${id}`
            };
        }
        const title = `${shop.name} - Nikotinpouches (Snus) in ${shop.city || 'Deutschland'} | Snus Finder`;
        const description = `Infos zu ${shop.name} in ${shop.city || ''}: Adresse, Öffnungszeiten, Sortiment für Nikotinpouches & Snus. Bewertungen lesen auf Snus Finder Deutschland.`;
        const canonicalUrl = `https://www.snus-finder.de/shops/${id}`;
        return { title, description, canonicalUrl };
    }, [shop, id, loading, error]);

    // Generate structured data for SEO
    const getStructuredData = useCallback(() => {
        if (!shop) {
            console.warn("getStructuredData wurde aufgerufen, bevor die Shop-Daten geladen waren.");
            return null; // Return null instead of {}
        }

        const openingHoursSpecification = shop.openingHours?.map(day => {
            if (!day.opens || !day.closes) return null;
            // Use ISO 8601 format for dayOfWeek if available, otherwise map?
            // Schema.org expects URLs like https://schema.org/Monday
            // Let's stick to the text name for now, Google might understand.
            return {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": day.dayOfWeek, // e.g., "Monday"
                "opens": day.opens,
                // Handle 24h case properly for schema (closes next day or 23:59:59)
                "closes": (day.opens === '00:00' && day.closes === '23:59') ? '23:59:59' : day.closes
            };
        }).filter(Boolean) || [];

        const shopImageUrl = shop.images && shop.images.length > 0
            ? shop.images[0].url
            : 'https://www.snus-finder.de/images/placeholder-shop.svg'; // Use your production URL

        // Use canonical URL
        const canonicalUrl = `https://www.snus-finder.de/shops/${id}`;

        return {
            "@context": "https://schema.org",
            "@type": shop.category === 'Online Shop' ? 'OnlineStore' : 'Store',
            "name": shop.name,
            "address": shop.address ? { // Only add address if it exists
                "@type": "PostalAddress",
                "streetAddress": shop.address, // Assuming shop.address is the street
                "addressLocality": shop.city,
                "postalCode": shop.postalCode,
                "addressCountry": "DE"
            } : undefined,
            "geo": shop.coordinates?.lat && shop.coordinates?.lng ? { // Only add geo if coordinates exist
                "@type": "GeoCoordinates",
                "latitude": shop.coordinates.lat,
                "longitude": shop.coordinates.lng
            } : undefined,
            "telephone": shop.phone || undefined, // Add only if phone exists
            "url": canonicalUrl, // Use canonical URL
            "image": shopImageUrl,
            "openingHoursSpecification": openingHoursSpecification.length > 0 ? openingHoursSpecification : undefined,
            "aggregateRating": shop.rating && shop.reviewCount ? {
                "@type": "AggregateRating",
                "ratingValue": shop.rating.toFixed(1),
                "reviewCount": shop.reviewCount
            } : undefined,
            // Add description for better context
            "description": `Informationen über ${shop.name} in ${shop.city || ''}, ein Geschäft für Snus und Nikotinbeutel. Adresse: ${shop.address || 'Nicht angegeben'}.`
        };
    }, [shop, id]); // Add id dependency for canonicalUrl

    // Funktion zum Öffnen des Claim-Modals
    const showClaimModal = () => {
        setSelectedPlanForClaim(null); // Auswahl zurücksetzen beim Öffnen
        setIsClaimModalVisible(true);
    };

    // Funktion zum Schließen des Claim-Modals
    const handleClaimModalCancel = () => {
        setIsClaimModalVisible(false);
    };

    // Funktion zur Auswahl des Plans im Claim-Modal
    const handlePlanSelectForClaim = (selectedPlanData) => {
        console.log("Plan selected in modal:", selectedPlanData);
        setSelectedPlanForClaim(selectedPlanData);
    };

    // Funktion zum Starten des Bezahlvorgangs für den Claim
    const handleInitiateClaimPayment = async () => {
        if (!selectedPlanForClaim || !selectedPlanForClaim.id) {
            notification.error({ message: 'Bitte wählen Sie zuerst einen Plan aus.' });
            return;
        }
        if (!shop || !shop._id) {
             notification.error({ message: 'Shop-Daten nicht verfügbar.' });
            return;
        }

        setClaimSubmitting(true);
        console.log(`Attempting to claim shop ${shop._id} with plan price ID: ${selectedPlanForClaim.id}`);
        try {
            const response = await api.post('/payments/create-claim-checkout-session', {
                priceId: selectedPlanForClaim.id,
                shopId: shop._id,
                successUrl: `${window.location.origin}/claim-success?session_id={CHECKOUT_SESSION_ID}&shop_id=${shop._id}`,
                cancelUrl: window.location.href, // Bleibt auf der Detailseite bei Abbruch
            });

            if (response.data && response.data.url) {
                // Leite zum Stripe Checkout weiter
                window.location.href = response.data.url;
            } else {
                notification.error({ message: 'Fehler beim Starten des Bezahlvorgangs.', description: response.data.message || 'Unbekannter Fehler' });
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            notification.error({ 
                message: 'Fehler beim Starten des Bezahlvorgangs.', 
                description: error.response?.data?.message || error.message || 'Bitte versuchen Sie es später erneut.'
            });
        } finally {
            setClaimSubmitting(false);
            // Modal muss nicht geschlossen werden, da zur Stripe-Seite weitergeleitet wird
        }
    };

    // Function to handle reporting inaccuracy
    const handleReportInaccuracy = async () => {
        if (!reportReason) {
            notification.warn({ message: 'Bitte geben Sie einen Grund für die Meldung an.'});
            return;
        }
        setReportSending(true);
        try {
            const response = await api.post(`/shops/${id}/report`, { reason: reportReason });
            if (response.status === 200) {
                notification.success({ message: 'Shop erfolgreich gemeldet.', description: 'Vielen Dank für Ihre Hilfe!' });
                setReportSent(true);
                setIsReportModalVisible(false);
                setReportReason('');
            } else {
                throw new Error(response.data.message || 'Fehler beim Melden des Shops.');
            }
        } catch (err) {
            console.error('Fehler beim Melden des Shops:', err);
            notification.error({ message: 'Fehler beim Melden', description: err.message || 'Bitte versuchen Sie es erneut.'});
        } finally {
            setReportSending(false);
        }
    };

    const showReportModal = () => {
        setIsReportModalVisible(true);
    };

    const handleReportModalCancel = () => {
        setIsReportModalVisible(false);
        setReportReason(''); // Reset reason on cancel
    };

    // Define formattedHours and isOpen here, AFTER confirming shop exists
    // but before the final return. They depend on shop.
    // These will only be calculated if loading is false and no error occurred.
    const formattedHours = useMemo(() => formatOpeningHours(shop?.openingHours), [shop, formatOpeningHours]);
    const isOpen = useMemo(() => isShopOpenNow(), [shop, isShopOpenNow]);

    const metaTags = generateMetaTags(); // Generate meta tags
    const structuredDataJSON = getStructuredData(); // Generate the JSON-LD data

    // Custom arrows for Carousel
    const SampleNextArrow = props => {
        const { className, style, onClick } = props;
        return (
            <div
                className={className}
                style={{ ...style, display: "block", background: "rgba(0, 0, 0, 0.5)", right: 10, borderRadius: '50%' }}
                onClick={onClick}
            ><RightOutlined style={{ color: 'white' }} /></div>
        );
    }

    const SamplePrevArrow = props => {
        const { className, style, onClick } = props;
        return (
            <div
                className={className}
                style={{ ...style, display: "block", background: "rgba(0, 0, 0, 0.5)", left: 10, zIndex: 1, borderRadius: '50%' }}
                onClick={onClick}
            ><LeftOutlined style={{ color: 'white' }} /></div>
        );
    }

    // Check if the current user is the owner
    // This is a basic check, assumes user object is stored in localStorage or context after login
    // A more robust check would involve comparing user ID from token/context with shop.ownerId
    let isOwner = false;
    try {
        const userData = JSON.parse(localStorage.getItem('user')); // Example: getting user data
        if (userData && shop.owner && userData.id === shop.owner) {
             isOwner = true;
             console.log("User is the owner of this shop.");
        }
    } catch (e) {
        console.error("Error checking owner status:", e);
    }

    // *** Start Loading/Error Handling ***
    if (loading) {
        return (
            <Layout>
                <Helmet>
                    <title>{metaTags.title}</title>
                    <meta name="description" content={metaTags.description} />
                    <link rel="canonical" href={metaTags.canonicalUrl} />
                </Helmet>
                <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 134px)' }}>
                    <Spin size="large" tip="Lade Shop-Details..." />
                </Content>
            </Layout>
        );
    }

    if (error || !shop) {
        return (
            <Layout>
                <Helmet>
                     <title>{metaTags.title}</title>
                     <meta name="description" content={metaTags.description} />
                     <link rel="canonical" href={metaTags.canonicalUrl} />
                 </Helmet>
                <Content style={{ padding: '50px' }}>
                    <Result
                        status="error"
                        title="Shop nicht gefunden"
                        subTitle={error || 'Der gesuchte Shop konnte nicht geladen oder gefunden werden.'}
                        extra={[
                            <Button type="primary" key="back" onClick={() => navigate(-1)}>Zurück</Button>,
                            <Button key="home" onClick={() => navigate('/')}>Zur Startseite</Button>
                        ]}
                    />
                </Content>
            </Layout>
        );
    }
    // *** End Loading/Error Handling ***

    // Render shop details
    return (
        <Layout>
            {/* Add Helmet for SEO Meta Tags */}
            <Helmet>
                <title>{metaTags.title}</title>
                <meta name="description" content={metaTags.description} />
                <link rel="canonical" href={metaTags.canonicalUrl} />
                {structuredDataJSON && (
                    <script type="application/ld+json">
                        {JSON.stringify(structuredDataJSON)}
                    </script>
                )}
            </Helmet>
            <Content style={{ padding: '20px 15px' }}> {/* Adjust padding */}

                {/* Section 1: Image Carousel */}
                <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                         <Card styles={{ body: { padding: 0 } }}>
                             {/* Wrap Carousel with Image.PreviewGroup */} 
                            <Image.PreviewGroup>
                                {shop.images && shop.images.length > 0 ? (
                                    <Carousel 
                                        arrows 
                                        prevArrow={<SamplePrevArrow />} 
                                        nextArrow={<SampleNextArrow />} 
                                        ref={carouselRef}
                                        dots={{ className: 'carousel-dots' }}
                                    >
                                        {shop.images.map((image, index) => (
                                            <div key={index}>
                                                <Image 
                                                    // Removed preview={{ visible: false }} to enable default preview
                                                    // preview={false} // Enable preview by default
                                                    src={image.url} 
                                                    alt={`${shop.name} Bild ${index + 1}`} 
                                                    style={{ width: '100%', height: 'auto', maxHeight: '450px', objectFit: 'cover', cursor: 'pointer' }} 
                                                />
                                            </div>
                                        ))}
                                    </Carousel>
                                ) : (
                                    // Placeholder image - no preview needed here
                                    <Image 
                                        preview={false} 
                                        src="/images/placeholder-shop.svg" 
                                        alt={`${shop.name} Platzhalterbild`} 
                                        style={{ width: '100%', height: 'auto', maxHeight: '300px', objectFit: 'contain', background: '#f0f2f5' }} 
                                    />
                                )}
                            </Image.PreviewGroup>
                        </Card>
                    </Col>
                </Row>
                
                {/* Section 2: Main Shop Info & Actions */}
                <Row justify="center" style={{ marginBottom: '30px' }}>
                     <Col xs={24} md={20} lg={18}>
                        <Card>
                            {/* Owner Badge/Link */}
                             {shop.owner && (
                                <Link to={`/user/${shop.owner._id}`}> {/* Assuming owner object has _id */} 
                                    <Tag icon={<UserOutlined />} color="blue" style={{ marginBottom: '10px', cursor: 'pointer' }}>
                                        Verifiziertes Mitglied: {shop.owner.username || 'Besitzer'} {/* Assuming owner object has username */}
                                    </Tag>
                                </Link>
                            )}
                            {/* Premium Badge */}
                            {shop.subscriptionPlan && shop.subscriptionPlan !== 'free' && (
                                <Tag icon={<StarFilled />} color="gold" style={{ marginBottom: '10px' }}>
                                    {shop.subscriptionPlan.charAt(0).toUpperCase() + shop.subscriptionPlan.slice(1)} Eintrag
                                </Tag>
                            )}
                            <Title level={2} style={{ marginTop: 0 }}>{shop.name}</Title>
                            <Space align="center" style={{ marginBottom: '15px' }}>
                                <Rate disabled allowHalf value={shop.rating || 0} style={{ fontSize: 16 }}/>
                                <Text type="secondary">({shop.reviewCount || 0} Bewertungen)</Text>
                                <Tag color={isOpen ? "green" : "red"} icon={<ClockCircleOutlined />}>
                                    {isOpen ? 'Jetzt geöffnet' : 'Jetzt geschlossen'}
                                </Tag>
                             </Space>
                             
                             <Paragraph>
                                <Space><EnvironmentOutlined /> {shop.address}, {shop.postalCode} {shop.city}</Space><br/>
                                {shop.phone && <><Space><PhoneOutlined /> <AntLink href={`tel:${shop.phone}`}>{shop.phone}</AntLink></Space><br/></>}
                                {shop.website && <><Space><GlobalOutlined /> <AntLink href={shop.website.startsWith('http') ? shop.website : `http://${shop.website}`} target="_blank" rel="noopener noreferrer">Website besuchen</AntLink></Space><br/></>}
                             </Paragraph>

                             {/* NEUE BESCHREIBUNG */}
                             {shop.description && (
                                 <Paragraph style={{ marginTop: '15px', fontStyle: 'italic', color: 'rgba(0, 0, 0, 0.65)' }}>
                                     {shop.description}
                                 </Paragraph>
                             )}

                             <Divider />

                             {/* Action Buttons */}
                             <Space wrap size="middle">
                                 {/* Nur Anzeigen wenn User eingeloggt UND Shop NICHT beansprucht UND User NICHT Besitzer */} 
                                 {isUserAuthenticated && !shop.owner && !isOwner && (
                                     <Button 
                                        type="primary" 
                                        icon={<CheckCircleOutlined />} 
                                        onClick={showClaimModal} 
                                        loading={isClaiming} 
                                    >
                                        Diesen Shop beanspruchen
                                    </Button>
                                 )}
                                 {/* Wenn User der Besitzer ist */}
                                 {isUserAuthenticated && isOwner && (
                                     <Button 
                                        type="primary" 
                                        onClick={() => navigate('/dashboard/my-shops')} 
                                    >
                                        Meinen Shop verwalten
                                    </Button>
                                 )}
                                  {/* Wenn Shop beansprucht, aber User NICHT Besitzer */} 
                                  {!isOwner && shop.owner && (
                                      <Tag color="blue">Dieser Shop wird bereits verwaltet.</Tag>
                                  )}

                                 {/* Report Button - Immer anzeigen, außer wenn schon gemeldet */}
                                 {!reportSent ? (
                                    <Button 
                                        icon={<ExclamationCircleOutlined />} 
                                        onClick={showReportModal}
                                        danger
                                        ghost
                                    >
                                        Ungenauigkeit melden
                                    </Button>
                                 ) : (
                                     <Tag color="success">Meldung erhalten</Tag>
                                 )}
                             </Space>
                        </Card>
                    </Col>
                </Row>

                 {/* Section 3: Details (Opening Hours & Brands) */}
                <Row justify="center" style={{ marginBottom: '30px' }}>
                    <Col xs={24} md={20} lg={18}>
                        <Card title="Details & Sortiment">
                            <Row gutter={[24, 24]}> {/* Gutter between hours and brands */}
                                <Col xs={24} md={12}>
                                    <Title level={4}><CalendarOutlined /> Öffnungszeiten</Title>
                                    <Descriptions bordered column={1} size="small">
                                        {orderedGermanDays.map(day => (
                                            <Descriptions.Item key={day} label={day}>{
                                                formattedHours[day] === 'Geschlossen' ? <Text type="danger">{formattedHours[day]}</Text> : 
                                                formattedHours[day] === '24 Stunden geöffnet' ? <Text type="success">{formattedHours[day]}</Text> : 
                                                formattedHours[day]
                                            }</Descriptions.Item>
                                        ))}
                                    </Descriptions>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Title level={4}><TagsOutlined /> Verfügbare Marken (Beispiele)</Title>
                                    {shop.brands && shop.brands.length > 0 ? (
                                        <BrandsList brands={shop.brands} />
                                    ) : (
                                        <Text type="secondary">Keine Markeninformationen verfügbar.</Text>
                                    )}
                                    {shop.acceptsOnlyCash && (
                                         <Alert message="Akzeptiert nur Bargeld" type="warning" showIcon style={{ marginTop: '15px' }}/>
                                     )}
                                     {shop.isDeliveryAvailable && (
                                         <Alert message="Lieferung verfügbar" type="info" showIcon style={{ marginTop: '15px' }}/>
                                     )}
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                 {/* Section 4: Map */}
                 {shop.coordinates && shop.coordinates.lat && shop.coordinates.lng && shop.category !== 'Online Shop' && (
                    <Row justify="center" style={{ marginBottom: '30px' }}>
                        <Col xs={24} md={20} lg={18}>
                            <Card title="Standort">
                                 <ShopMap 
                                    shop={shop} // Pass the single shop object
                                />
                            </Card>
                        </Col>
                    </Row>
                 )}

                 {/* Section 5: Reviews */}
                 <Row justify="center" style={{ marginBottom: '30px' }}>
                     <Col xs={24} md={20} lg={18}>
                         <Card title="Bewertungen">
                            <ReviewSection 
                                shopId={id} 
                                initialReviews={reviews} 
                                onReviewAdded={handleReviewAdded} 
                                isAuthenticated={isUserAuthenticated} // Pass auth status
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Claim Modal */}
                 <Modal
                    title={<><CheckCircleOutlined /> Shop beanspruchen & upgraden</>}
                    open={isClaimModalVisible}
                    onCancel={handleClaimModalCancel}
                    footer={[
                        <Button key="back" onClick={handleClaimModalCancel}>
                            Abbrechen
                        </Button>,
                        <Button 
                            key="submit"
                            type="primary" 
                            loading={claimSubmitting}
                            onClick={handleInitiateClaimPayment}
                            disabled={!selectedPlanForClaim || claimSubmitting} // Disable if no plan selected or submitting
                        >
                            Weiter zur Bezahlung
                        </Button>,
                    ]}
                    width={800} // Wider Modal for plans
                >
                    <Paragraph>
                        Wählen Sie einen Plan, um {shop?.name || 'diesen Shop'} zu beanspruchen und erweiterte Funktionen freizuschalten (z.B. Detailbearbeitung, mehr Bilder, hervorgehobene Darstellung).
                    </Paragraph>
                     {/* Pass the required props to NewPricingPlans */}
                    <NewPricingPlans 
                        onSelect={handlePlanSelectForClaim} 
                        selectedPlanId={selectedPlanForClaim?.id} // Pass only the ID
                        context="claim" // Indicate the context is claiming
                    />
                    <Divider />
                    <Paragraph type="secondary" style={{ fontSize: '0.9em', textAlign: 'center'}}>
                        Sie werden zu unserem sicheren Zahlungsanbieter Stripe weitergeleitet. Ihr Abonnement wird nach erfolgreicher Zahlung aktiv.
                    </Paragraph>
                 </Modal>

                {/* Report Modal */}
                <Modal
                    title={<><ExclamationCircleOutlined /> Ungenauigkeit melden</>}
                    open={isReportModalVisible}
                    onOk={handleReportInaccuracy}
                    onCancel={handleReportModalCancel}
                    confirmLoading={reportSending}
                    okText="Meldung senden"
                    cancelText="Abbrechen"
                >
                    <Paragraph>Ist eine Information über "{shop?.name || 'diesen Shop'}" nicht korrekt? Bitte beschreiben Sie kurz, was nicht stimmt.</Paragraph>
                    <TextArea 
                        rows={4} 
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        placeholder="z.B. Öffnungszeiten sind falsch, Shop existiert nicht mehr, falsche Adresse..."
                    />
                </Modal>

            </Content>
        </Layout>
    );
};

export default ShopDetailPage;